import React, { useEffect, useState, useContext, Suspense, lazy } from 'react';
import './app.scss';
import { Content, Loading, Theme } from '@carbon/react';
import { Route, Switch, withRouter, Redirect } from 'react-router-dom';
import {
  ToastNotificationContextProvider,
  AlertBoxContextProvider,
  AlertBoxContext,
} from './content-mui/context';
import ProtectedRoute from './content/common/ProtectedRoute';
import auth from './content/common/auth';
import {
  ROLES_ACCOUNT_ADMIN,
  ROLES_ACCOUNT_ANY,
  USERROLE,
} from './content/common/constants';
import { CAPABILITY, PRODUCT_PROFILE } from './content/common/productProfiles';
import { getLandingPath } from './content/common/utils';
import { ConfigurationContextProvider } from './content-mui/Ingestion/context/ConfigurationContext';
import api from './Api';
import CurrentUser from './services/CurrentUser';
import OktaService from './services/OktaService';
import MatchReviewGroupDetail from './content/MergeReview/MergeReviewGroupDetail';
import LoginPage from './content-mui/LoginPage';
import LoginCallback from './content-mui/LoginPage/LoginCallback';

import {
  BatchWizardProvider,
  BatchWizardContext,
} from './components/AskDaAgent/BatchWizardContext';

// Lazy imports
const TutorialHeader = lazy(() => import('./components/TutorialHeader'));
const GenieChat = lazy(() => import('./components/GenieChat/GenieChat'));
const OperationalDashboardPage = lazy(() =>
  import('./content-mui/OperationalDashboard')
);
const NewConfig = lazy(() =>
  import('./content-mui/Ingestion/NewConfig/NewConfig')
);
const ConfigDashboardPage = lazy(() =>
  import('./content-mui/ConfigDashboard/ConfigDashboardPage')
);
const MonitoringPage = lazy(() => import('./content-mui/Monitoring'));
const ClientSettings = lazy(() =>
  import('./content/ClientSettings/ClientSettingsPage')
);
const UserProfile = lazy(() => import('./content-mui/UserProfile'));
const UsersPage = lazy(() => import('./content-mui/Administration/Users'));
const Analytics = lazy(() => import('./content/AnalyticsPage/Analytics'));
const ViewConfig = lazy(() => import('./content-mui/Ingestion/ViewConfig'));
const CompanyPage = lazy(() =>
  import('./content-mui/Administration/Companies')
);
const IdentityResolution = lazy(() =>
  import('./content/IdentityResolution/IdentityResolution')
);
const IdentityResolutionRealTime = lazy(() =>
  import('./content/IdentityResolution/IdentityResolutionRealTime')
);
const SourceSelectionPage = lazy(() =>
  import('./content-mui/Ingestion/pages/SourceSelectionPage')
);
const NotificationsPage = lazy(() =>
  import('./content-mui/Notifications/NotificationsPage')
);
const SourceConnectors = lazy(() =>
  import('./content-mui/Connectors/SourceConnectors')
);
const MatchAILens = lazy(() =>
  import('./content/IdentityResolution/MatchAILens')
);
const IdentityResolutionDashboard = lazy(() =>
  import('./content-mui/IdentityResolution')
);
const SpineGraph = lazy(() =>
  import('./content/IdentityResolution/SpineGraph')
);
const DataQualityManager = lazy(() =>
  import('./content-mui/DataQualityManager/DataQualityManager')
);
const DataQualityManagerRecord = lazy(() =>
  import('./content-mui/DataQualityManager/DataQualityManagerRecord')
);
const DataQualityManagerRejectedRecords = lazy(() =>
  import('./content-mui/DataQualityManager/DataQualityManagerRejectedRecords')
);

const MergeReview = lazy(() => import('./content/MergeReview/MergeReview'));
const MergeReviewGroupDetail = lazy(() =>
  import('./content/MergeReview/MergeReviewGroupDetail')
);

const DPConfigurationPage = lazy(() =>
  import('./content/Delivery/DPConfigurationPage')
);
const MetaStorePage = lazy(() => import('./content/MetaStore/MetaStorePage'));
const DataExtractsPage = lazy(() =>
  import('./content/DataExtracts/DataExtractsPage')
);
const APIsPage = lazy(() => import('./content/APIs/APIsPage'));
const LookupPage = lazy(() => import('./content-mui/Lookup/LookupPage'));
const AttributeWeightagePage = lazy(() =>
  import('./content-mui/AttributeWeightage')
);

// DQH Pages (Material UI)
const MuiThemeProvider = lazy(() =>
  import('./content-mui/providers/MuiThemeProvider')
);
const BatchProcessesPage = lazy(() =>
  import('./content-mui/DQH/BatchProcesses')
);
const CreateProcessPage = lazy(() => import('./content-mui/DQH/CreateProcess'));
// Intelligence Genie
const IntelligenceConsole = lazy(() =>
  import(
    './components/intelligenceGenie/IntelligenceConsole/IntelligenceConsole'
  )
);
const IntelligenceGenieMyAudiences = lazy(() =>
  import('./components/intelligenceGenie/MyAudiences/MyAudiences')
);
const IntelligenceGenieDashboards = lazy(() =>
  import('./components/intelligenceGenie/Dashboards/Dashboards')
);
const IntelligenceGenieSettings = lazy(() =>
  import('./components/intelligenceGenie/Settings/Settings')
);
const ReportingPage = lazy(() =>
  import('./content-mui/Reporting/ReportingPage')
);
const MatchOutputRecords = lazy(() =>
  import('./content-mui/MatchOutputRecords')
);
const RiskAnalysisPage = lazy(() => import('./content-mui/Insights'));

// Standardized Fields / Validation Rules (global configuration) - Material UI
const StandardizedFieldsListPage = lazy(() =>
  import('./content-mui/StandardizedFields/StandardizedFieldsListPage')
);
const StandardizedFieldFormPage = lazy(() =>
  import('./content-mui/StandardizedFields/StandardizedFieldFormPage')
);
const ValidationRulesListPage = lazy(() =>
  import('./content-mui/StandardizedFields/ValidationRulesListPage')
);
const ValidationRuleFormPage = lazy(() =>
  import('./content-mui/StandardizedFields/ValidationRuleFormPage')
);
const AddValidationRulePage = lazy(() =>
  import('./content-mui/StandardizedFields/AddValidationRulePage')
);
const MftFileHistoryDashboard = lazy(() =>
  import('./content-mui/StandardizedFields/MftFileHistoryDashboard')
);

/** Capability routes: any of the 4 roles may open; write vs read is API/UI. */
const ANY_ROLE = ROLES_ACCOUNT_ANY;
/** Administration → Users. */
const ADMIN_ROLES = ROLES_ACCOUNT_ADMIN;

const StandardizedFieldsListRoute = props => (
  <MuiThemeProvider>
    <StandardizedFieldsListPage {...props} />
  </MuiThemeProvider>
);
const StandardizedFieldFormRoute = props => (
  <MuiThemeProvider>
    <StandardizedFieldFormPage {...props} />
  </MuiThemeProvider>
);
const ValidationRulesListRoute = props => (
  <MuiThemeProvider>
    <ValidationRulesListPage {...props} />
  </MuiThemeProvider>
);
const ValidationRuleFormRoute = props => (
  <MuiThemeProvider>
    <ValidationRuleFormPage {...props} />
  </MuiThemeProvider>
);
const MftFileHistoryDashboardRoute = props => (
  <MuiThemeProvider>
    <MftFileHistoryDashboard {...props} />
  </MuiThemeProvider>
);
const AddValidationRuleRoute = props => (
  <MuiThemeProvider>
    <AddValidationRulePage {...props} />
  </MuiThemeProvider>
);
const UsersPageRoute = props => (
  <MuiThemeProvider>
    <UsersPage {...props} />
  </MuiThemeProvider>
);
const CompanyPageRoute = props => (
  <MuiThemeProvider>
    <CompanyPage {...props} />
  </MuiThemeProvider>
);
const MonitoringPageRoute = props => (
  <MuiThemeProvider>
    <MonitoringPage {...props} />
  </MuiThemeProvider>
);
const NotificationsPageRoute = props => (
  <MuiThemeProvider>
    <NotificationsPage {...props} />
  </MuiThemeProvider>
);
const OperationalDashboardPageRoute = props => (
  <MuiThemeProvider>
    <OperationalDashboardPage {...props} />
  </MuiThemeProvider>
);
const SourceConnectorsRoute = props => (
  <MuiThemeProvider>
    <SourceConnectors {...props} />
  </MuiThemeProvider>
);

const DataQualityManagerRoute = props => (
  <MuiThemeProvider>
    <DataQualityManager {...props} />
  </MuiThemeProvider>
);
const DataQualityManagerRejectedRecordsRoute = props => (
  <MuiThemeProvider>
    <DataQualityManagerRejectedRecords {...props} />
  </MuiThemeProvider>
);
const DataQualityManagerRecordRoute = props => (
  <MuiThemeProvider>
    <DataQualityManagerRecord {...props} />
  </MuiThemeProvider>
);

const LookupPageRoute = props => (
  <MuiThemeProvider>
    <LookupPage {...props} />
  </MuiThemeProvider>
);

const RiskAnalysisRoute = props => (
  <MuiThemeProvider>
    <RiskAnalysisPage {...props} />
  </MuiThemeProvider>
);

/**
 * Bridge component: registers React Router history with BatchWizardContext.
 * Rendered inside BatchWizardProvider so it can access the context, and
 * receives history from the parent App (which gets it via withRouter).
 */
const BatchHistoryBridge = ({ history }) => {
  const batchCtx = useContext(BatchWizardContext);

  useEffect(() => {
    if (batchCtx?.registerHistory) {
      batchCtx.registerHistory(history);
    }
  }, [history, batchCtx]);

  return null;
};

function App(props) {
  const [user, setUser] = useState({
    first_name: '',
    last_name: '',
    email: '',
  });
  const [loading, setLoading] = useState(true);
  const { setAlert } = useContext(AlertBoxContext);
  const [key, setKey] = useState(0);

  // Helper: load user profile and navigate
  const loadUserProfile = async () => {
    const userProfile = await api.getUserProfile();
    CurrentUser.setUser(userProfile.data);
    setUser({
      user_id: userProfile.data.user_id,
      first_name: userProfile.data.firstname,
      last_name: userProfile.data.lastname,
      email: userProfile.data.email,
      phone_number: userProfile.data.phone_number,
      title: userProfile.data.title,
      clients: userProfile.data.user_companies.map(company => ({
        company: company.company.company_name,
        company_id: company.company.company_id,
        default: company?.default,
        roles: company?.roles,
        product_profile:
          company.company?.product_profile || PRODUCT_PROFILE.STANDARD,
      })),
    });
    auth.login(() => {
      props.history.push(
        props.location.pathname === '/login' ||
          props.location.pathname === '/' ||
          props.location.pathname === '/login/callback'
          ? '/homePage'
          : props.location.pathname
      );
    });
  };

  useEffect(() => {
    const loadApp = async () => {
      const path = props.location.pathname;
      if (path === '/login/callback') {
        setLoading(false);
        return;
      }
      if (path === '/login') {
        setLoading(false);
        return;
      }

      try {
        const authenticated = await OktaService.init();
        if (authenticated) {
          await loadUserProfile();
        } else {
          props.history.push('/login');
        }
      } catch (error) {
        console.error(error);
        props.history.push('/login');
      }
      setLoading(false);
    };
    loadApp();
  }, []);

  const reloadNotifications = () => {
    localStorage.removeItem('notifications_filters');
    setKey(prevKey => prevKey + 1); // Change the key to force remount
  };

  if (loading) {
    return <Loading active={loading} />;
  }
  const isAuthPage =
    props.location.pathname === '/login' ||
    props.location.pathname === '/login/callback';

  return (
    <>
      <Suspense fallback={<Loading active />}>
        <BatchWizardProvider>
          <BatchHistoryBridge history={props.history} />
          <ToastNotificationContextProvider>
            <AlertBoxContextProvider user={user}>
              <Theme theme="g100">
                <TutorialHeader
                  user={user}
                  reloadNotifications={reloadNotifications}
                />
              </Theme>
              {!isAuthPage && <GenieChat />}
              <Switch>
                <Route
                  exact
                  path="/login"
                  render={() => (
                    <MuiThemeProvider>
                      <LoginPage setUser={setUser} />
                    </MuiThemeProvider>
                  )}
                />
                <Route
                  exact
                  path="/login/callback"
                  render={() => (
                    <MuiThemeProvider>
                      <LoginCallback setUser={setUser} />
                    </MuiThemeProvider>
                  )}
                />

                <Content className="content">
                  {/* DQH Routes */}
                  <ProtectedRoute
                    path="/dqh/create-process"
                    requiredCapability={CAPABILITY.DQH_HOME}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={props => (
                      <ConfigurationContextProvider>
                        <MuiThemeProvider>
                          <CreateProcessPage {...props} />
                        </MuiThemeProvider>
                      </ConfigurationContextProvider>
                    )}
                  />
                  <ProtectedRoute
                    path="/dqh/batch-processes"
                    requiredCapability={CAPABILITY.DQH_HOME}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={props => (
                      <MuiThemeProvider>
                        <BatchProcessesPage {...props} />
                      </MuiThemeProvider>
                    )}
                  />
                  <ProtectedRoute
                    exact
                    path="/dqh"
                    requiredCapability={CAPABILITY.DQH_HOME}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={props => (
                      <MuiThemeProvider>
                        <BatchProcessesPage {...props} />
                      </MuiThemeProvider>
                    )}
                  />

                  {/* Standardized Fields / Validation Rules (global) - Material UI */}
                  <ProtectedRoute
                    exact
                    path="/standardized-fields"
                    requiredCapability={CAPABILITY.STD_FIELDS_FIELDS_RULES}
                    component={StandardizedFieldsListRoute}
                    allowedRoles={ANY_ROLE}
                    user={user}
                  />
                  <ProtectedRoute
                    exact
                    path="/standardized-fields/new"
                    requiredCapability={CAPABILITY.STD_FIELDS_FIELDS_RULES}
                    component={StandardizedFieldFormRoute}
                    allowedRoles={ANY_ROLE}
                    user={user}
                  />
                  <ProtectedRoute
                    exact
                    path="/standardized-fields/:fieldId/edit"
                    requiredCapability={CAPABILITY.STD_FIELDS_FIELDS_RULES}
                    component={StandardizedFieldFormRoute}
                    allowedRoles={ANY_ROLE}
                    user={user}
                  />
                  <ProtectedRoute
                    exact
                    path="/standardized-fields/validation-rules/new"
                    requiredCapability={CAPABILITY.STD_FIELDS_FIELDS_RULES}
                    component={AddValidationRuleRoute}
                    allowedRoles={ANY_ROLE}
                    user={user}
                  />
                  <ProtectedRoute
                    exact
                    path="/standardized-fields/:fieldId/validation-rules"
                    requiredCapability={CAPABILITY.STD_FIELDS_FIELDS_RULES}
                    component={ValidationRulesListRoute}
                    allowedRoles={ANY_ROLE}
                    user={user}
                  />
                  <ProtectedRoute
                    exact
                    path="/standardized-fields/:fieldId/validation-rules/new"
                    requiredCapability={CAPABILITY.STD_FIELDS_FIELDS_RULES}
                    component={ValidationRuleFormRoute}
                    allowedRoles={ANY_ROLE}
                    user={user}
                  />
                  <ProtectedRoute
                    exact
                    path="/standardized-fields/:fieldId/validation-rules/:ruleId/edit"
                    requiredCapability={CAPABILITY.STD_FIELDS_FIELDS_RULES}
                    component={ValidationRuleFormRoute}
                    allowedRoles={ANY_ROLE}
                    user={user}
                  />
                  <ProtectedRoute
                    exact
                    path="/standardized-fields/mft-file-history"
                    requiredCapability={CAPABILITY.INSIGHTS_FILE_MONITORING}
                    component={MftFileHistoryDashboardRoute}
                    allowedRoles={ANY_ROLE}
                    user={user}
                  />
                  <ProtectedRoute
                    path="/homePage"
                    component={IdentityResolutionDashboard}
                    allowedRoles={ANY_ROLE}
                    user={user}
                  />
                  <ProtectedRoute
                    path="/operationalDashboard"
                    requiredCapability={
                      CAPABILITY.INSIGHTS_OPERATIONAL_DASHBOARD
                    }
                    component={OperationalDashboardPageRoute}
                    allowedRoles={ANY_ROLE}
                    user={user}
                  />
                  <ProtectedRoute
                    path="/ingestion/dashboard"
                    requiredCapability={CAPABILITY.INGESTION_CONFIGURATION}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={() => (
                      <MuiThemeProvider>
                        <ConfigDashboardPage user={user} />
                      </MuiThemeProvider>
                    )}
                  />
                  <ProtectedRoute
                    exact
                    path="/identityResolution"
                    requiredCapability={CAPABILITY.IDR_GRAPH}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={IdentityResolution}
                  />
                  <ProtectedRoute
                    path="/identityResolution-real-time"
                    requiredCapability={CAPABILITY.IDR_COOKIE_LOOKUP}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={IdentityResolutionRealTime}
                  />
                  <ProtectedRoute
                    path="/client-spine-graph"
                    requiredCapability={CAPABILITY.IDR_CLIENT_SPINE}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={SpineGraph}
                  />
                  <ProtectedRoute
                    path="/data-quality-manager"
                    requiredCapability={CAPABILITY.INSIGHTS_DATA_QUALITY}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={DataQualityManagerRoute}
                  />
                  <Route
                    path="/data-manager/rejected-records/:recordId"
                    component={DataQualityManagerRejectedRecordsRoute}
                  />
                  <Route
                    path="/data-manager/records/:recordId"
                    component={DataQualityManagerRecordRoute}
                  />

                  <ProtectedRoute
                    path="/merge-review"
                    requiredCapability={CAPABILITY.INSIGHTS_MERGE_MANAGER}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={MergeReview}
                  />
                  <ProtectedRoute
                    path="/merge-review-group/:groupId"
                    requiredCapability={CAPABILITY.INSIGHTS_MERGE_MANAGER}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={MergeReviewGroupDetail}
                  />
                  <ProtectedRoute
                    path="/matchAILens"
                    requiredCapability={CAPABILITY.IDR_MATCH_AI_LENS}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={MatchAILens}
                  />
                  <ProtectedRoute
                    path="/airflowMonitor"
                    requiredCapability={CAPABILITY.MONITORING_AIRFLOW}
                    component={MonitoringPageRoute}
                    allowedRoles={ANY_ROLE}
                    user={user}
                  />
                  <ProtectedRoute
                    path="/monitoring"
                    requiredCapability={CAPABILITY.MONITORING_AIRFLOW}
                    component={MonitoringPageRoute}
                    allowedRoles={ANY_ROLE}
                    user={user}
                  />
                  <ProtectedRoute
                    path="/analytics"
                    requiredCapability={CAPABILITY.INSIGHTS_CONSOLE}
                    component={Analytics}
                    allowedRoles={ANY_ROLE}
                    user={user}
                  />
                  <ConfigurationContextProvider>
                    <ProtectedRoute
                      path="/ingestion/newConfig"
                      requiredCapability={CAPABILITY.INGESTION_CONFIGURATION}
                      allowedRoles={ANY_ROLE}
                      user={user}
                      component={props => (
                        <MuiThemeProvider>
                          <NewConfig {...props} />
                        </MuiThemeProvider>
                      )}
                    />
                    <ProtectedRoute
                      path="/ingestion/sourceSelection"
                      requiredCapability={CAPABILITY.INGESTION_CONFIGURATION}
                      allowedRoles={ANY_ROLE}
                      user={user}
                      component={SourceSelectionPage}
                    />
                    <ProtectedRoute
                      path="/ingestion/view/:id"
                      requiredCapability={CAPABILITY.INGESTION_CONFIGURATION}
                      allowedRoles={ANY_ROLE}
                      user={user}
                      component={props => (
                        <MuiThemeProvider>
                          <ViewConfig {...props} />
                        </MuiThemeProvider>
                      )}
                    />
                    <ProtectedRoute
                      path="/ingestion/edit/:id"
                      requiredCapability={CAPABILITY.INGESTION_CONFIGURATION}
                      allowedRoles={ANY_ROLE}
                      user={user}
                      component={props => (
                        <ConfigurationContextProvider>
                          <MuiThemeProvider>
                            <NewConfig {...props} />
                          </MuiThemeProvider>
                        </ConfigurationContextProvider>
                      )}
                    />
                  </ConfigurationContextProvider>
                  <ProtectedRoute
                    path="/ingestion/connectors"
                    requiredCapability={[
                      CAPABILITY.INGESTION_CONNECTORS,
                      CAPABILITY.DQH_CONNECTORS_INBOUND,
                    ]}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    connectorType={'INBOUND'}
                    component={SourceConnectorsRoute}
                  />
                  <ProtectedRoute
                    path="/identityResolution/attribute-weightage"
                    requiredCapability={CAPABILITY.IDR_ATTRIBUTE_WEIGHTAGE}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={props => (
                      <MuiThemeProvider>
                        <AttributeWeightagePage {...props} />
                      </MuiThemeProvider>
                    )}
                  />

                  <ProtectedRoute
                    path="/clientSettings"
                    requiredCapability={CAPABILITY.ADMIN_COMPANY_SETTINGS}
                    allowedRoles={[USERROLE.SuperAdmin]}
                    user={user}
                    component={ClientSettings}
                  />

                  <Route
                    path="/userProfile"
                    render={() => (
                      <MuiThemeProvider>
                        <UserProfile user={user} setUser={setUser} />
                      </MuiThemeProvider>
                    )}
                  />
                  <ProtectedRoute
                    path="/destination"
                    requiredCapability={[
                      CAPABILITY.DELIVERY_OUTBOUND_CONNECTORS,
                      CAPABILITY.DQH_CONNECTORS_OUTBOUND,
                    ]}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    setUser={setUser}
                    connectorType={'OUTBOUND'}
                    component={SourceConnectorsRoute}
                  />
                  <ProtectedRoute
                    path="/users"
                    requiredCapability={CAPABILITY.ADMIN_USERS}
                    allowedRoles={ADMIN_ROLES}
                    user={user}
                    setUser={setUser}
                    component={UsersPageRoute}
                  />
                  <ProtectedRoute
                    path="/companies"
                    requiredCapability={CAPABILITY.ADMIN_COMPANIES}
                    allowedRoles={[USERROLE.SuperAdmin]}
                    user={user}
                    setUser={setUser}
                    component={CompanyPageRoute}
                  />
                  <ProtectedRoute
                    path="/notifications"
                    allowedRoles={ANY_ROLE}
                    user={user}
                    key={key}
                    component={NotificationsPageRoute}
                  />
                  <ProtectedRoute
                    path="/delivery/DPConfiguration"
                    requiredCapability={CAPABILITY.ORCHESTRATION_HUB}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={DPConfigurationPage}
                  />
                  <ProtectedRoute
                    path="/metaStore"
                    requiredCapability={CAPABILITY.DELIVERY_META_STORE}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={MetaStorePage}
                  />
                  <ProtectedRoute
                    path="/dataExtracts"
                    requiredCapability={CAPABILITY.DELIVERY_DATA_EXTRACTS}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={DataExtractsPage}
                  />
                  <ProtectedRoute
                    path="/apis"
                    requiredCapability={CAPABILITY.DELIVERY_APIS}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={APIsPage}
                  />
                  <ProtectedRoute
                    path="/lookup"
                    requiredCapability={CAPABILITY.DELIVERY_LOOKUP}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={LookupPageRoute}
                  />

                  <ProtectedRoute
                    exact
                    path="/matchOutputRecords"
                    requiredCapability={
                      CAPABILITY.BUSINESS_HIERARCHY_MATCH_OUTPUT
                    }
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={MatchOutputRecords}
                  />
                  <Route
                    exact
                    path="/insights/corporate-family-grouping-quality"
                    render={() => <Redirect to="/insights/risk-analysis" />}
                  />
                  <ProtectedRoute
                    exact
                    path="/insights/risk-analysis"
                    requiredCapability={CAPABILITY.INSIGHTS_RISK_ANALYSIS}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={RiskAnalysisRoute}
                  />

                  {/* Intelligence Genie */}
                  <ProtectedRoute
                    exact
                    path="/intelligenceGenie/intelligenceConsole"
                    requiredCapability={CAPABILITY.KG_INTELLIGENCE_CONSOLE}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={IntelligenceConsole}
                  />
                  <ProtectedRoute
                    exact
                    path="/intelligenceGenie/myAudiences"
                    requiredCapability={CAPABILITY.KG_MY_AUDIENCES}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={IntelligenceGenieMyAudiences}
                  />
                  <ProtectedRoute
                    exact
                    path="/intelligenceGenie/dashboards"
                    requiredCapability={CAPABILITY.KG_DASHBOARDS}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={IntelligenceGenieDashboards}
                  />
                  <ProtectedRoute
                    exact
                    path="/intelligenceGenie/settings"
                    requiredCapability={CAPABILITY.KG_SETTINGS}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={IntelligenceGenieSettings}
                  />
                  <ProtectedRoute
                    exact
                    path="/reporting"
                    requiredCapability={CAPABILITY.REPORTING_HOME}
                    allowedRoles={ANY_ROLE}
                    user={user}
                    component={ReportingPage}
                  />
                </Content>
              </Switch>
            </AlertBoxContextProvider>
          </ToastNotificationContextProvider>
        </BatchWizardProvider>
      </Suspense>
    </>
  );
}

export default withRouter(App);
